import { useQueryClient } from '@tanstack/react-query';
import { Image, Loader2, Plus, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { actions } from '../../actions';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../components/ui/use-toast';
import { getSupportedTokenSymbols, getTokenAddress } from '../../config/tokenConfig';
import propertyFactoryABI from '../../contractData/abis/propertyFactory.json';
import getcontractAddress from '../../contractData/contractAddress/addresses';
import { getEventInputsForDecoding } from '../../helper/abiHelper';
import { compressImage } from '../../helper/function';
import { services } from '../../services';
import { sendTransactionHash } from '../../services/transactionApi';
import { createAdminNotification } from '../../services/notificationService';
import { AssetFormData } from '../../types/asset';
import {
  CategoryRecord,
  getCategoryDisplayName,
  getCategoryId,
  resolveAssetTypeIdByCategoryName,
} from '../../utils/categoryUtils';
import { getCurrencyContractAddress, toBlockchainUnits } from '../../utils/currencyUtils';
import {
  calculateTotalAssetAppreciation,
  calculateTotalRentalIncome,
  validateFinancialInputs,
} from '../../utils/financialCalculations';
import { web3 } from '../../web3';

/**
 * ASSET CREATION MODAL - FINANCIAL INTEGRITY & CURRENCY CONSISTENCY
 *
 * CRITICAL FINANCIAL SECURITY FIX:
 * ================================
 * - REMOVED: Random Math.random() calculations for financial metrics
 * - ADDED: Proper financial calculation utilities based on user inputs
 * - VALIDATION: Financial inputs validated for reasonable ranges
 * - CALCULATIONS: Rental income = Total Value × (Asset Yield / 100)
 * - APPRECIATION: Asset Appreciation = Total Value × (Investment Return / 100)
 *
 * FINANCIAL MODEL:
 * ================
 * - Asset Yield: Annual rental yield percentage (0-100%)
 * - Investment Return: Total expected return percentage (0-500%)
 * - Rental Income: Calculated from asset yield × total token value
 * - Asset Appreciation: Calculated from investment return × total token value
 * - All calculations are deterministic and based on user-provided data
 *
 * IMPORTANT CHANGES IMPLEMENTED:
 * ==============================
 *
 * 1. CURRENCY CONSISTENCY FIXES:
 * -----------------------------
 * - All financial calculations now use validated currency utilities
 * - Blockchain conversions respect token decimal precision
 * - Currency validation prevents USD/USDT mismatch errors
 * - Exchange rate assumptions are explicitly documented
 *
 * 2. RENTAL DATA MODEL REFACTORING:
 * ---------------------------------
 * - Rental data is now sent as structured fields instead of concatenated strings
 * - Each field (unit, tenant, startDate, paymentDay) is stored separately
 * - Enables proper querying, filtering, and UI management
 *
 * CURRENCY UNITS EXPECTED:
 * ========================
 * - pricePerToken: In selected paymentToken units (e.g., 1.5 USDT)
 * - minPurchase/maxPurchase: In paymentToken units
 * - All blockchain operations: Converted to Wei equivalents
 * - Backend receives: Human-readable amounts in paymentToken currency
 *
 * TOKEN CONFIGURATION:
 * ===================
 * - Uses dynamic token configuration loaded from environment variables
 * - Supports multiple blockchain networks (BSC Mainnet/Testnet, Ethereum, etc.)
 * - Automatically adapts to current network specified in REACT_APP_NETWORK
 * - Fallback to hardcoded addresses for backward compatibility
 *
 * BACKEND API REQUIREMENTS:
 * =========================
 * 1. Currency Validation:
 *    - Accept buyTokenSymbol field for currency validation
 *    - Validate all monetary amounts are in the specified currency
 *
 * 2. Rental Data Structure:
 *    - Accept individual rental fields instead of concatenated descriptions
 *    - Store each field in dedicated database columns
 *    - Validate date formats and numeric ranges
 *
 * CRITICAL VALIDATION:
 * ====================
 * - Never mix USD and USDT amounts in calculations
 * - Always validate currency compatibility before math operations
 * - Use currency utilities for all blockchain conversions
 */

interface AddAssetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd?: (asset: AssetFormData) => void;
}

// Dynamic payment token configuration loaded from environment
const paymentTokens = getSupportedTokenSymbols();

// Legacy payment token addresses mapping (fallback)
// TODO: Remove this once fully migrated to dynamic configuration
const paymentTokenAddresses: Record<string, string> = {
  USDT:
    process.env.REACT_APP_TOKEN_USDT_BSC_MAINNET || '0x55d398326f99059fF775485246999027B3197955',
  USDC:
    process.env.REACT_APP_TOKEN_USDC_BSC_MAINNET || '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
  DAI: process.env.REACT_APP_TOKEN_DAI_BSC_MAINNET || '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3',
  ETH: process.env.REACT_APP_TOKEN_ETH_BSC_MAINNET || '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
};

export function AddAssetModal({ open, onOpenChange, onAdd }: AddAssetModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [formData, setFormData] = useState<AssetFormData>({
    name: '',
    categoryId: '',
    location: '',
    valuation: 0,
    tokenSymbol: '',
    totalTokens: 0,
    description: '',
    maxSupply: 0,
    paymentToken: 'USDT',
    minPurchase: 0,
    maxPurchase: 0,
    pricePerToken: 0,
    ownerAddress: '',
    platformFeeRecipient: '',
    platformFeePercentage: 0,
    startDate: '',
    endDate: '',
    countryCode: '',
    city: '',
    assetYield: 0,
    minHoldingDuration: 0,
    locationUrl: '',
    youtubeUrl: '',
    facilities: [],
    totalRentalIncome: 0,
    totalAppreciation: 0,
    investmentReturn: 0,
    propertyImage: null,
    additionalImages: [],
    assetViewDocument: null,
    roomsAmenitiesDocument: null,
    floorplansDocument: null,
    isFeatured: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assetTypesList, setAssetTypesList] = useState<Array<{ _id: string; name: string }>>(
    []
  );
  const [categoriesList, setCategoriesList] = useState<CategoryRecord[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [amenitiesList, setAmenitiesList] = useState<
    Array<{ id?: string; _id?: string; name?: string; amenityName?: string }>
  >([]);
  const [web3Data, setWeb3Data] = useState<{ accounts?: string[] } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [nftContractInstance, setNftContractInstance] = useState<any>(null);
  const [propertyImages, setPropertyImages] = useState<
    Array<{ file: File | null; description: string }>
  >([]);
  const propertyImageRef = useRef<HTMLInputElement>(null);
  const additionalImagesRef = useRef<HTMLInputElement>(null);
  const [currentTransactionCurrency, setCurrentTransactionCurrency] = useState<{
    symbol: string;
    contractAddress: string;
  } | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      const response = await actions.getAssetCategories();
      const categories = response.data || [];
      setCategoriesList(categories);
      setFormData((prev) => {
        if (prev.categoryId || categories.length === 0) return prev;
        return { ...prev, categoryId: getCategoryId(categories[0]) };
      });
    } catch {
      toast({
        description: 'Failed to load categories',
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Fetch categories and amenities when modal opens
  useEffect(() => {
    if (open) {
      loadCategories();

      // Fetch asset types (Villa, Hotel, Row House) for marketplace badge mapping
      actions
        .getCategories()
        .then((response) => {
          if (response?.data) {
            setAssetTypesList(response.data);
          }
        })
        .catch(() => {
          // Error fetching asset types
        });

      // Fetch amenities
      actions
        .getAmenities()
        .then((response) => {
          if (response?.data?.data) {
            setAmenitiesList(response.data.data);
          }
        })
        .catch(() => {
          // Error fetching amenities
        });

      // Initialize Web3
      initializeWeb3();
    }
  }, [open, loadCategories]);

  const handleAddCategory = async () => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      toast({ description: 'Enter a category name', variant: 'destructive' });
      return;
    }

    setIsAddingCategory(true);
    try {
      const created = await actions.createCategory(trimmedName);
      setNewCategoryName('');
      await loadCategories();
      setFormData((prev) => ({ ...prev, categoryId: getCategoryId(created) }));
      toast({ description: `Category "${trimmedName}" added`, variant: 'default' });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to add category. It may already exist.';
      toast({ description: message, variant: 'destructive' });
    } finally {
      setIsAddingCategory(false);
    }
  };

  const initializeWeb3 = async () => {
    try {
      const web3Data = await services.getWeb3();
      if (web3Data && web3Data.accounts && web3Data.accounts.length > 0) {
        setWeb3Data(web3Data);
        const contractInstance = await actions.getPropertyFactoryContract();
        setNftContractInstance(contractInstance);
      }
    } catch (error) {
      // Error initializing Web3
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nameOk = formData.name.trim().length > 0;
    const symbolOk = formData.tokenSymbol.trim().length > 0;
    const countryOk = formData.countryCode.trim().length > 0;
    const cityOk = formData.city.trim().length > 0;
    if (!nameOk || !symbolOk || !countryOk || !cityOk) {
      toast({ description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    if (!formData.propertyImage) {
      toast({ description: 'Please upload a property image', variant: 'destructive' });
      return;
    }

    if (!formData.categoryId) {
      toast({ description: 'Please select a category', variant: 'destructive' });
      return;
    }

    // Validate financial inputs
    if (formData.assetYield === undefined || formData.assetYield < 0 || formData.assetYield > 100) {
      toast({
        description: 'Please enter a valid asset yield between 0% and 100%',
        variant: 'destructive',
      });
      return;
    }

    if (
      formData.investmentReturn === undefined ||
      formData.investmentReturn < 0 ||
      formData.investmentReturn > 500
    ) {
      toast({
        description: 'Please enter a valid investment return between 0% and 500%',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Mint asset on blockchain
      await mintAssetOnBlockchain();

      // Step 2: Create asset in backend (this will be called from onReceipt)
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      const err = error as { message?: string; reason?: string };
      const detail =
        axiosError?.response?.data?.message ||
        err?.reason ||
        err?.message ||
        'Failed to create asset';
      toast({
        description: `Create failed: ${detail}`,
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  const mintAssetOnBlockchain = async () => {
    if (!nftContractInstance || !web3Data?.accounts?.[0]) {
      throw new Error('Contract instance or wallet not initialized. Please connect your wallet.');
    }

    // Validate dates
    const startTime = new Date(formData.startDate);
    const endTime = new Date(formData.endDate);
    if (startTime >= endTime) {
      throw new Error('Start time must be before end time');
    }

    // Convert timestamps to Unix format (seconds)
    const startTimeUnix = Math.floor(startTime.getTime() / 1000);
    const endTimeUnix = Math.floor(endTime.getTime() / 1000);

    /**
     * CURRENCY VALIDATION AND CONVERSION
     *
     * IMPORTANT: All financial calculations must use consistent currency units.
     * The system assumes all amounts are in the selected payment token currency.
     *
     * Currency Units Expected:
     * - pricePerToken: In payment token units (e.g., 1.5 = 1.5 USDT)
     * - minPurchase/maxPurchase: In payment token units
     * - All blockchain operations: Converted to appropriate precision (Wei)
     */
    const selectedCurrency = formData.paymentToken;

    // Validate that we have a contract address for the selected currency
    // Use dynamic configuration with fallback to legacy mapping
    const buyTokenAddress =
      getTokenAddress(selectedCurrency) ||
      getCurrencyContractAddress(selectedCurrency) ||
      paymentTokenAddresses[selectedCurrency];
    if (!buyTokenAddress) {
      throw new Error(
        `Invalid or unsupported payment token: ${selectedCurrency} on current network`
      );
    }

    // Store currency information for use in callbacks
    setCurrentTransactionCurrency({
      symbol: selectedCurrency,
      contractAddress: buyTokenAddress,
    });

    // Validate currency values are positive and reasonable
    if (formData.pricePerToken <= 0) {
      throw new Error(`Invalid token price: ${formData.pricePerToken}. Must be greater than 0.`);
    }

    if (formData.minPurchase < 0 || formData.maxPurchase < 0) {
      throw new Error('Purchase amounts cannot be negative');
    }

    if (formData.minPurchase > formData.maxPurchase) {
      throw new Error('Minimum purchase cannot be greater than maximum purchase');
    }

    // Format all parameters according to contract requirements
    // Convert all amounts to blockchain units (Wei equivalent)
    const params = [
      formData.name, // name
      formData.tokenSymbol, // symbol
      toBlockchainUnits(formData.maxSupply || formData.totalTokens, selectedCurrency), // maxTotalSupply
      startTimeUnix, // startTime
      endTimeUnix, // saleEndDate
      web3.utils.toChecksumAddress(buyTokenAddress), // buyToken
      toBlockchainUnits(formData.minPurchase, selectedCurrency), // minPurchaseAmount
      toBlockchainUnits(formData.maxPurchase, selectedCurrency), // maxPurchaseAmount
      toBlockchainUnits(formData.pricePerToken, selectedCurrency), // assetPrice
      web3.utils.toChecksumAddress(formData.ownerAddress), // assetOwner
      web3.utils.toChecksumAddress(formData.platformFeeRecipient), // platformFeeRecipient
      formData.platformFeePercentage, // platformFeePercentage
      formData.minHoldingDuration, // minHoldingDuration
      formData.assetYield, // assetYield
    ];

    // Call the contract function
    await nftContractInstance.methods
      .createPropertyAsset(...params)
      .send({
        from: web3Data.accounts[0],
        gas: 3000000,
        gasPrice: web3.utils.toWei('20', 'gwei'),
      })
      .on('transactionHash', async (hash: string) => {
        try {
          await sendTransactionHash(hash);
        } catch (apiError) {
          // Error sending transaction hash to backend
        }
      })
      .on(
        'receipt',
        async (receipt: {
          transactionHash?: string;
          logs?: Array<{ data?: string; topics?: string[] }>;
        }) => {
          await onReceipt(receipt);
        }
      )
      .on('error', (error: Error) => {
        const e = error as Error & { reason?: string };
        toast({
          description: `Transaction failed: ${e?.reason || error.message}`,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      });
  };

  const onReceipt = async (receipt: {
    transactionHash?: string;
    logs?: Array<{ data?: string; topics?: string[] }>;
  }) => {
    try {
      // Extract deployed property address from transaction logs
      let deployedPropertyAddress = null;

      if (receipt.logs && receipt.logs.length > 0) {
        // Get the PropertyAssetCreated event inputs from the ABI
        const propertyAssetCreatedInputs = getEventInputsForDecoding(
          propertyFactoryABI,
          'PropertyAssetCreated'
        );

        for (const log of receipt.logs) {
          try {
            const decodedLog = web3.eth.abi.decodeLog(propertyAssetCreatedInputs, log.data, [
              log.topics[1],
            ]);
            deployedPropertyAddress = decodedLog.asset;
            break;
          } catch (decodeError) {
            continue;
          }
        }
      }

      // Set rental address if property was deployed
      if (deployedPropertyAddress) {
        try {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const propertyContract = await actions.getPropertyAssetContract(deployedPropertyAddress);
          const rentalPoolAddress = getcontractAddress().rentalPool;
          if (rentalPoolAddress && web3.utils.isAddress(rentalPoolAddress)) {
            await propertyContract.methods
              .setRental(rentalPoolAddress)
              .send({ from: web3Data?.accounts?.[0] || '' })
              .on('transactionHash', async (hash: string) => {
                await sendTransactionHash(hash);
              });
          }
        } catch (error) {
          // Error setting rental address
        }
      }

      /**
       * FINANCIAL CALCULATIONS - DETERMINISTIC & VALIDATED
       *
       * SECURITY FIX: Replaced Math.random() with proper financial formulas
       * ========================================================
       * PREVIOUS (DANGEROUS): Random percentages for demo purposes
       * CURRENT (SECURE): Deterministic calculations based on user inputs
       *
       * FORMULAS:
       * - Total Rental Income = Total Token Value × (Asset Yield / 100)
       * - Total Asset Appreciation = Total Token Value × (Investment Return / 100)
       *
       * VALIDATION:
       * - Asset Yield: 0-100% (reasonable rental yields)
       * - Investment Return: 0-500% (high-growth scenarios allowed)
       * - Total Token Value: Must be > 0
       *
       * All calculations assume consistent currency units:
       * - formData.pricePerToken: In selected payment token units (e.g., 1.5 USDT)
       * - formData.maxSupply/totalTokens: Token count (dimensionless)
       * - calcTotalTokenValue: Total value in payment token currency
       */
      const calcTotalTokenValue =
        (formData.maxSupply || formData.totalTokens) * formData.pricePerToken;

      // Validate the total value is reasonable
      if (calcTotalTokenValue <= 0) {
        throw new Error('Invalid total token value for financial calculations');
      }

      // Validate financial inputs before calculations
      const financialInputs = {
        totalTokenValue: calcTotalTokenValue,
        assetYield: formData.assetYield || 0,
        investmentReturn: formData.investmentReturn || 0,
      };

      const validation = validateFinancialInputs(financialInputs);
      if (!validation.isValid) {
        throw new Error(`Financial validation failed: ${validation.errors.join(', ')}`);
      }

      // Calculate financial metrics using proper business logic
      const calcTotalRentalIncome = calculateTotalRentalIncome(financialInputs);
      const calcTotalAssetAppreciation = calculateTotalAssetAppreciation(financialInputs);

      const selectedCategory = categoriesList.find(
        (category) => getCategoryId(category) === formData.categoryId
      );
      const selectedCategoryName = getCategoryDisplayName(selectedCategory);

      // Prepare property data for backend with currency information
      const propertyData = {
        assetName: formData.name,
        symbol: formData.tokenSymbol,
        maxSupply: formData.maxSupply || formData.totalTokens,
        assetPrice: formData.pricePerToken.toString(),
        minPurchaseToken: formData.minPurchase.toString(),
        maxPurchaseToken: formData.maxPurchase.toString(),
        buyTokenAddress:
          currentTransactionCurrency?.contractAddress ||
          getTokenAddress(formData.paymentToken) ||
          paymentTokenAddresses[formData.paymentToken], // Use dynamic or stored contract address
        buyTokenSymbol: currentTransactionCurrency?.symbol || formData.paymentToken, // Use stored currency symbol
        ownerAddress: formData.ownerAddress,
        platformFeeRecepient: formData.platformFeeRecipient,
        platformFeePercentage: formData.platformFeePercentage.toString(),
        minHoldingDuration: formData.minHoldingDuration,
        annualYield: formData.assetYield,
        // Convert the admin's local date/time into an absolute UTC instant so the
        // backend stores one unambiguous moment regardless of server/user timezone.
        startTime: new Date(formData.startDate).toISOString(),
        saleEndDate: new Date(formData.endDate).toISOString(),
        countryCode: formData.countryCode,
        city: formData.city,
        locationUrl: formData.locationUrl,
        socialYtUrl: formData.youtubeUrl,
        assetAddress: deployedPropertyAddress,
        txHash: receipt.transactionHash,
        status: 'APPROVED',
        category: formData.categoryId,
        assetType: resolveAssetTypeIdByCategoryName(selectedCategoryName, assetTypesList),
        isFeatured: formData.isFeatured === true,
      };

      // Create FormData for backend
      const backendFormData = new FormData();

      // Add main image
      if (formData.propertyImage) {
        const compressedImage = await compressImage(formData.propertyImage);
        backendFormData.append('mainImage', compressedImage);
      }

      // Add side images
      propertyImages.forEach((img, index) => {
        if (img.file) {
          backendFormData.append('sideImages', img.file);
          backendFormData.append(`sideImageDescription_${index}`, img.description || '');
        }
      });

      // Add documents
      if (formData.assetViewDocument) {
        backendFormData.append('assetOverview', formData.assetViewDocument);
      }
      if (formData.roomsAmenitiesDocument) {
        backendFormData.append('roomsAmenities', formData.roomsAmenitiesDocument);
      }
      if (formData.floorplansDocument) {
        backendFormData.append('assetFloorplans', formData.floorplansDocument);
      }

      // Prepare main data object
      const mainData = {
        ...propertyData,
        AssetDetials: {
          detials: {
            description: formData.description,
            facilities: formData.facilities,
          },
          financial: {
            totalShare: formData.totalTokens,
            totalTokenValue: calcTotalTokenValue,
            totalRentalIncome: calcTotalRentalIncome,
            totalAssetAppreciation: calcTotalAssetAppreciation,
            investmentReturn: formData.investmentReturn,
          },
        },
      };

      backendFormData.append('data', JSON.stringify(mainData));

      // Send to backend
      // Note: Don't set Content-Type header for FormData - axios will set it automatically with the correct boundary
      const response = await services.post('asset/create', backendFormData);

      if (response?.status === 200 || response?.status === 201) {
        toast({
          description: `Asset ${formData.name} created successfully`,
          variant: 'default',
        });
        createAdminNotification({
          type: 'asset',
          title: 'Asset created',
          message: `"${formData.name}" was created and listed.`,
          priority: 'medium',
        });
        resetForm();
        queryClient.invalidateQueries({ queryKey: ['assets'] });
        const derivedLocation = [formData.city, formData.countryCode]
          .map((s) => s.trim())
          .filter(Boolean)
          .join(', ');
        onAdd?.({ ...formData, location: derivedLocation });
        onOpenChange(false);
        navigate('/assets');
      } else {
        throw new Error('Failed to create asset');
      }
    } catch (error: unknown) {
      toast({
        description: 'Error processing asset creation',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      categoryId: '',
      location: '',
      valuation: 0,
      tokenSymbol: '',
      totalTokens: 0,
      description: '',
      maxSupply: 0,
      paymentToken: 'USDT',
      minPurchase: 0,
      maxPurchase: 0,
      pricePerToken: 0,
      ownerAddress: '',
      platformFeeRecipient: '',
      platformFeePercentage: 0,
      startDate: '',
      endDate: '',
      countryCode: '',
      city: '',
      assetYield: 0,
      minHoldingDuration: 0,
      locationUrl: '',
      youtubeUrl: '',
      facilities: [],
      totalRentalIncome: 0,
      totalAppreciation: 0,
      investmentReturn: 0,
      propertyImage: null,
      additionalImages: [],
      assetViewDocument: null,
      roomsAmenitiesDocument: null,
      floorplansDocument: null,
      isFeatured: false,
    });
    setPropertyImages([]);
  };

  const handlePropertyImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, propertyImage: file });
    }
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newImages = files.map((file) => ({ file, description: '' }));
    setPropertyImages([...propertyImages, ...newImages]);
  };

  const removeAdditionalImage = (index: number) => {
    setPropertyImages(propertyImages.filter((_, i) => i !== index));
  };

  const handleDocumentChange = (
    field: 'assetViewDocument' | 'roomsAmenitiesDocument' | 'floorplansDocument',
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, [field]: file });
    }
  };

  const toggleFacility = (facilityId: string) => {
    if (formData.facilities.includes(facilityId)) {
      setFormData({ ...formData, facilities: formData.facilities.filter((f) => f !== facilityId) });
    } else {
      setFormData({ ...formData, facilities: [...formData.facilities, facilityId] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[900px] max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle>Create New Asset</DialogTitle>
          <DialogDescription>Add a new tokenized asset to your portfolio.</DialogDescription>
        </DialogHeader>

        <ScrollArea className='max-h-[70vh] pr-4'>
          <form onSubmit={handleSubmit} className='space-y-6 py-4'>
            {/* Basic Info */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='name'>
                  Asset Name <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='name'
                  placeholder='E.g., Alex Apartment Complex'
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='description'>Description</Label>
                <Textarea
                  id='description'
                  placeholder='Enter asset description'
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={1}
                />
              </div>
            </div>

            {/* Token Info */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='tokenSymbol'>
                  Symbol <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='tokenSymbol'
                  placeholder='3-5 letters (e.g., LUXAP)'
                  maxLength={5}
                  value={formData.tokenSymbol}
                  onChange={(e) =>
                    setFormData({ ...formData, tokenSymbol: e.target.value.toUpperCase() })
                  }
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='maxSupply'>Max Supply</Label>
                <Input
                  id='maxSupply'
                  type='number'
                  placeholder='Total number of tokens to be created (e.g., 1000)'
                  value={formData.maxSupply || ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setFormData({ ...formData, maxSupply: value, totalTokens: value });
                  }}
                />
              </div>
            </div>

            {/* Payment & Purchase */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label>Payment Token</Label>
                <Select
                  value={formData.paymentToken}
                  onValueChange={(value) => setFormData({ ...formData, paymentToken: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select payment token' />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentTokens.map((token) => (
                      <SelectItem key={token} value={token}>
                        {token}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='minPurchase'>Min. Purchase Token</Label>
                <Input
                  id='minPurchase'
                  type='number'
                  placeholder='Minimum tokens per investor (e.g., 100)'
                  value={formData.minPurchase || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, minPurchase: Number(e.target.value) })
                  }
                />
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='maxPurchase'>Max. Purchase Token</Label>
                <Input
                  id='maxPurchase'
                  type='number'
                  placeholder='Maximum tokens per investor (e.g., 10000)'
                  value={formData.maxPurchase || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, maxPurchase: Number(e.target.value) })
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='pricePerToken'>Price Per Asset Token</Label>
                <Input
                  id='pricePerToken'
                  type='number'
                  step='0.01'
                  placeholder='Price in USDT (e.g., 1.00 = 1 USDT)'
                  value={formData.pricePerToken || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, pricePerToken: Number(e.target.value) })
                  }
                />
              </div>
            </div>

            {/* Wallet Addresses */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='ownerAddress'>Owner Address</Label>
                <Input
                  id='ownerAddress'
                  placeholder="Property owner's wallet (0x...)"
                  value={formData.ownerAddress}
                  onChange={(e) => setFormData({ ...formData, ownerAddress: e.target.value })}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='platformFeeRecipient'>Platform Fee Recipient</Label>
                <Input
                  id='platformFeeRecipient'
                  placeholder='Platform fee wallet address (0x...)'
                  value={formData.platformFeeRecipient}
                  onChange={(e) =>
                    setFormData({ ...formData, platformFeeRecipient: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Fee & Category */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='platformFeePercentage'>Platform Fee Percentage</Label>
                <Input
                  id='platformFeePercentage'
                  type='number'
                  placeholder='Enter fee % (between 0 to 100, e.g., 80)'
                  value={formData.platformFeePercentage || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, platformFeePercentage: Number(e.target.value) })
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label>
                  Category <span className='text-destructive'>*</span>
                </Label>
                <Select
                  value={formData.categoryId || undefined}
                  onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select category' />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesList.map((category) => {
                      const categoryId = getCategoryId(category);
                      return (
                        <SelectItem key={categoryId} value={categoryId}>
                          {getCategoryDisplayName(category)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <div className='flex gap-2'>
                  <Input
                    placeholder='Add new category'
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleAddCategory();
                      }
                    }}
                  />
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => void handleAddCategory()}
                    disabled={isAddingCategory || !newCategoryName.trim()}
                  >
                    {isAddingCategory ? <Loader2 className='w-4 h-4 animate-spin' /> : 'Add'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='startDate'>Start Date & Time</Label>
                <Input
                  id='startDate'
                  type='datetime-local'
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
                <p className='text-xs text-muted-foreground'>
                  Select when the property sale will begin
                </p>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='endDate'>End Date & Time</Label>
                <Input
                  id='endDate'
                  type='datetime-local'
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
                <p className='text-xs text-muted-foreground'>
                  Select when the property sale will end
                </p>
              </div>
            </div>

            {/* Location */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='countryCode'>Country Code</Label>
                <Input
                  id='countryCode'
                  placeholder='2-letter code (e.g., US, GB, SG)'
                  maxLength={2}
                  value={formData.countryCode}
                  onChange={(e) =>
                    setFormData({ ...formData, countryCode: e.target.value.toUpperCase() })
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='city'>City</Label>
                <Input
                  id='city'
                  placeholder='City name (e.g., New York)'
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
            </div>

            {/* Yield & Duration */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='assetYield'>Asset Yield (%)</Label>
                <Input
                  id='assetYield'
                  type='number'
                  min='0'
                  max='100'
                  step='0.01'
                  placeholder='Expected annual yield % (0-100, e.g., 8.5)'
                  value={formData.assetYield || ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value >= 0 && value <= 100) {
                      setFormData({ ...formData, assetYield: value });
                    }
                  }}
                />
                <p className='text-xs text-muted-foreground'>
                  Annual rental yield percentage based on property income
                </p>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='minHoldingDuration'>Minimum Holding Duration</Label>
                <Input
                  id='minHoldingDuration'
                  type='number'
                  placeholder='Minimum Holding Period (in Seconds)'
                  value={formData.minHoldingDuration || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, minHoldingDuration: Number(e.target.value) })
                  }
                />
              </div>
            </div>

            {/* URLs */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='locationUrl'>Location URL</Label>
                <Input
                  id='locationUrl'
                  placeholder='https://maps.google.com/...'
                  value={formData.locationUrl}
                  onChange={(e) => setFormData({ ...formData, locationUrl: e.target.value })}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='youtubeUrl'>YouTube URL</Label>
                <Input
                  id='youtubeUrl'
                  placeholder='https://youtube.com/...'
                  value={formData.youtubeUrl}
                  onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                />
              </div>
            </div>

            {/* Facilities */}
            {amenitiesList.length > 0 && (
              <div className='space-y-2'>
                <Label>Facilities</Label>
                <div className='flex flex-wrap gap-2'>
                  {amenitiesList.map((amenity) => (
                    <Button
                      key={amenity.id || amenity._id}
                      type='button'
                      variant={
                        formData.facilities.includes(amenity.id || amenity._id)
                          ? 'default'
                          : 'outline'
                      }
                      size='sm'
                      onClick={() => toggleFacility(amenity.id || amenity._id)}
                    >
                      {amenity.name || amenity.amenityName}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Financial Metrics */}
            <div className='grid grid-cols-3 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='totalRentalIncome'>Total Rental Income</Label>
                <Input
                  id='totalRentalIncome'
                  type='number'
                  placeholder='Expected annual rental income'
                  value={formData.totalRentalIncome || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, totalRentalIncome: Number(e.target.value) })
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='totalAppreciation'>Total Asset Appreciation</Label>
                <Input
                  id='totalAppreciation'
                  type='number'
                  placeholder='Expected value increase in USDT'
                  value={formData.totalAppreciation || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, totalAppreciation: Number(e.target.value) })
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='investmentReturn'>Investment Return (%)</Label>
                <Input
                  id='investmentReturn'
                  type='number'
                  min='0'
                  max='500'
                  step='0.01'
                  placeholder='Expected total return % (0-500, e.g., 25.0)'
                  value={formData.investmentReturn || ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value >= 0 && value <= 500) {
                      setFormData({ ...formData, investmentReturn: value });
                    }
                  }}
                />
                <p className='text-xs text-muted-foreground'>
                  Expected total investment return including appreciation
                </p>
              </div>
            </div>

            {/* Property Asset Image */}
            <div className='space-y-2'>
              <Label>
                Property Asset Image <span className='text-destructive'>*</span>
              </Label>
              <div
                className='border-2 border-dashed border-border rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 transition-colors'
                onClick={() => propertyImageRef.current?.click()}
              >
                <input
                  ref={propertyImageRef}
                  type='file'
                  accept='image/*'
                  className='hidden'
                  onChange={handlePropertyImageChange}
                />
                {formData.propertyImage ? (
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <Image className='w-5 h-5 text-primary' />
                      <span className='text-sm text-foreground'>{formData.propertyImage.name}</span>
                    </div>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({ ...formData, propertyImage: null });
                      }}
                    >
                      <X className='w-4 h-4' />
                    </Button>
                  </div>
                ) : (
                  <div className='text-muted-foreground'>
                    <Upload className='w-6 h-6 mx-auto mb-2' />
                    <p className='text-sm'>Browse... No file selected.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Property Images */}
            <div className='space-y-3'>
              <Label>Additional Property Images</Label>
              {propertyImages.length > 0 && (
                <div className='grid grid-cols-4 gap-3 mb-3'>
                  {propertyImages.map((img, index) => (
                    <div key={index} className='relative group'>
                      <div className='aspect-square rounded-lg bg-muted flex items-center justify-center overflow-hidden'>
                        {img.file ? (
                          <img
                            src={URL.createObjectURL(img.file)}
                            alt={`Additional ${index + 1}`}
                            className='w-full h-full object-cover'
                          />
                        ) : (
                          <Image className='w-8 h-8 text-muted-foreground' />
                        )}
                      </div>
                      <Button
                        type='button'
                        variant='destructive'
                        size='icon'
                        className='absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6'
                        onClick={() => removeAdditionalImage(index)}
                      >
                        <X className='w-3 h-3' />
                      </Button>
                      <Input
                        type='text'
                        placeholder='Description'
                        value={img.description}
                        onChange={(e) => {
                          const updated = [...propertyImages];
                          updated[index].description = e.target.value;
                          setPropertyImages(updated);
                        }}
                        className='mt-2 text-xs'
                      />
                    </div>
                  ))}
                </div>
              )}
              <div
                className='border-2 border-dashed border-border rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 transition-colors'
                onClick={() => additionalImagesRef.current?.click()}
              >
                <input
                  ref={additionalImagesRef}
                  type='file'
                  accept='image/*'
                  multiple
                  className='hidden'
                  onChange={handleAdditionalImagesChange}
                />
                <Plus className='w-5 h-5 mx-auto mb-1 text-muted-foreground' />
                <p className='text-sm text-muted-foreground'>Add New Image</p>
                <p className='text-xs text-muted-foreground'>Upload a property image</p>
              </div>
            </div>

            {/* Asset Documents */}
            <div className='space-y-4'>
              <Label className='text-base font-semibold'>Asset Documents</Label>

              <div className='space-y-2'>
                <Label htmlFor='assetViewDoc' className='text-sm'>
                  Asset View Document
                </Label>
                <div className='flex items-center gap-2'>
                  <Input
                    type='file'
                    id='assetViewDoc'
                    accept='.pdf,.doc,.docx'
                    onChange={(e) => handleDocumentChange('assetViewDocument', e)}
                  />
                  {formData.assetViewDocument && (
                    <span className='text-xs text-muted-foreground truncate max-w-[150px]'>
                      {formData.assetViewDocument.name}
                    </span>
                  )}
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='roomsDoc' className='text-sm'>
                  Rooms & Amenities Document
                </Label>
                <div className='flex items-center gap-2'>
                  <Input
                    type='file'
                    id='roomsDoc'
                    accept='.pdf,.doc,.docx'
                    onChange={(e) => handleDocumentChange('roomsAmenitiesDocument', e)}
                  />
                  {formData.roomsAmenitiesDocument && (
                    <span className='text-xs text-muted-foreground truncate max-w-[150px]'>
                      {formData.roomsAmenitiesDocument.name}
                    </span>
                  )}
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='floorplansDoc' className='text-sm'>
                  Asset Floorplans
                </Label>
                <div className='flex items-center gap-2'>
                  <Input
                    type='file'
                    id='floorplansDoc'
                    accept='.pdf,.doc,.docx,.png,.jpg'
                    onChange={(e) => handleDocumentChange('floorplansDocument', e)}
                  />
                  {formData.floorplansDocument && (
                    <span className='text-xs text-muted-foreground truncate max-w-[150px]'>
                      {formData.floorplansDocument.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className='flex items-start gap-3 rounded-lg border border-border/50 p-4'>
              <Checkbox
                id='isFeatured'
                checked={formData.isFeatured === true}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isFeatured: checked === true })
                }
              />
              <div className='space-y-1'>
                <Label htmlFor='isFeatured' className='cursor-pointer'>
                  Mark as featured investment
                </Label>
                <p className='text-xs text-muted-foreground'>
                  Only one asset can be featured at a time. This replaces any existing featured
                  asset.
                </p>
              </div>
            </div>

            <DialogFooter className='pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                    Creating...
                  </>
                ) : (
                  'Create Asset'
                )}
              </Button>
            </DialogFooter>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
